import { useCallback, useRef, useState } from 'react';
import { FormatListBulletedOutlined } from '@mui/icons-material';
import {
	ConfirmModal,
	FormActions,
	FormCard,
	FormSectionTitle,
	PageHeader,
	useConfirmModal,
	useToast,
} from '@practium/ui';
import type { FormPageMode } from '@practium/ui';
import type {
	BindingTab,
	UserPageMode,
	BindingFarmerFormData,
	BindingConsultantFormData,
} from '../types';
import { FarmerForm } from '../components/Forms/FarmerForm/FarmerForm';
import type { FarmerFormRef } from '../components/Forms/FarmerForm/FarmerForm';
import { ConsultantForm } from '../components/Forms/ConsultantForm/ConsultantForm';
import type { ConsultantFormRef } from '../components/Forms/ConsultantForm/ConsultantForm';
import { AvatarCard } from '../components/AvatarCard/AvatarCard';
import { BINDING_USER_TITLES, BINDING_USER_SUBTITLES, BINDING_USER_SECTION_TITLES } from '../constants/page.constants';
import styles from './AddUserPage.module.css';
import { useDeleteBindingUser } from '@practium/features/api';

const BINDING_TYPE = {
	farmers: 1,
	consultants: 2,
} as const satisfies Record<BindingTab, number>;

export interface AddBindingUserPageProps {
	type: BindingTab;
	onBack: () => void;
	initialMode?: UserPageMode;
	initialData?: BindingFarmerFormData | BindingConsultantFormData;
	onDelete?: () => void;
	canEdit?: boolean;
	bindingUserId?: string;
	avatarUrl?: string;
	onEnterProfile?: () => void;
}
export const AddBindingUserPage = ({
	type,
	onBack,
	initialMode = 'add',
	initialData,
	onDelete,
	canEdit = true,
	bindingUserId,
	avatarUrl,
	onEnterProfile,
}: AddBindingUserPageProps) => {
	const [currentMode, setCurrentMode] = useState<UserPageMode>(initialMode);
	const [isLoading, setIsLoading] = useState(false);
	const [avatarFile, setAvatarFile] = useState<File | null>(null);
	const { mutateAsync: deleteBindingUser } = useDeleteBindingUser();

	const farmerRef = useRef<FarmerFormRef>(null);
	const consultantRef = useRef<ConsultantFormRef>(null);

	const {
		confirm,
		isOpen: isConfirmOpen,
		options: confirmOptions,
		handleConfirm,
		handleCancel: handleConfirmCancel,
	} = useConfirmModal();

	const { success: toastSuccess, error: toastError } = useToast();

	const handleSubmit = useCallback(() => {
		onBack();
	}, [onBack]);

	const isFormDirty = useCallback(() => {
		if (avatarFile) return true;
		if (type === 'farmers') return farmerRef.current?.isDirty?.() ?? false;
		return consultantRef.current?.isDirty?.() ?? false;
	}, [avatarFile, type]);

	const handleCancel = useCallback(async () => {
		const revert = () => {
			setAvatarFile(null);
			if (currentMode === 'edit' && initialMode === 'view') {
				setCurrentMode('view');
			} else {
				onBack();
			}
		};

		if (!isFormDirty()) {
			revert();
			return;
		}

		const confirmed = await confirm({
			title: 'Cancelar alterações',
			message: 'Deseja descartar as alterações não salvas?',
			confirmText: 'Descartar',
			cancelText: 'Continuar editando',
			variant: 'warning',
		});
		if (!confirmed) return;
		revert();
	}, [confirm, currentMode, initialMode, isFormDirty, onBack]);

	const handleDelete = useCallback(async () => {
		const confirmed = await confirm({
			title: 'Excluir cadastro',
			message: 'Tem certeza que deseja excluir este cadastro? Esta ação não pode ser desfeita.',
			confirmText: 'Excluir',
			cancelText: 'Cancelar',
			variant: 'danger',
		});
		if (confirmed && bindingUserId) {
			setIsLoading(true);
			try {
				await deleteBindingUser({
					bindingType: BINDING_TYPE[type],
					id: bindingUserId,
				});
				toastSuccess('Cadastro excluído com sucesso.');
				onDelete?.();
			} catch (error) {
				toastError(error instanceof Error ? error.message : 'Erro ao excluir o cadastro.');
			} finally {
				setIsLoading(false);
			}
		}
	}, [bindingUserId, confirm, deleteBindingUser, onDelete, toastError, toastSuccess, type]);

	const handleEdit = useCallback(() => setCurrentMode('edit'), []);

	const handleSave = useCallback(() => {
		if (type === 'farmers') farmerRef.current?.submit();
		else consultantRef.current?.submit();
	}, [type]);

	const formMode: FormPageMode = currentMode;
	const title = BINDING_USER_TITLES[type][currentMode];
	const subtitle = BINDING_USER_SUBTITLES[currentMode][type];
	const sectionTitle = BINDING_USER_SECTION_TITLES[type];

	const pageActions = (
		<div className={styles.pageActions}>
			<FormActions
				mode={formMode}
				onCancel={handleCancel}
				onSave={handleSave}
				isLoading={isLoading}
				onDelete={onDelete ? handleDelete : undefined}
				onView={currentMode === 'view' ? onEnterProfile : undefined}
				onEdit={canEdit ? handleEdit : undefined}
			/>
		</div>
	);

	const avatarCard = (
		<AvatarCard
			mode={currentMode}
			file={avatarFile}
			onFileChange={setAvatarFile}
			personName={
				initialData ? `${initialData.name ?? ''} ${initialData.surName ?? ''}`.trim() : undefined
			}
			avatarUrl={avatarUrl}
			onEditRequest={canEdit ? handleEdit : undefined}
		/>
	);

	return (
		<div className={styles.page}>
			<PageHeader title={title} subtitle={subtitle} onBack={onBack} />
			<div className={styles.cards}>
				<div className={styles.mainContent}>
					<FormCard
						icon={<FormatListBulletedOutlined />}
						title="Informações Pessoais"
						className={styles.mainCard}
					>
						<div className={styles.formContent}>
							<FormSectionTitle>{sectionTitle}</FormSectionTitle>
							<div className={styles.fields}>
								{type === 'farmers' && (
									<FarmerForm
										ref={farmerRef}
										mode={currentMode}
										initialData={initialData as BindingFarmerFormData | undefined}
										onSubmit={handleSubmit}
										onLoadingChange={setIsLoading}
									/>
								)}
								{type === 'consultants' && (
									<ConsultantForm
										ref={consultantRef}
										mode={currentMode}
										initialData={initialData as BindingConsultantFormData | undefined}
										onSubmit={handleSubmit}
										onLoadingChange={setIsLoading}
									/>
								)}
							</div>
						</div>
					</FormCard>
					{pageActions}
				</div>
				{avatarCard}
			</div>

			<ConfirmModal
				isOpen={isConfirmOpen}
				title={confirmOptions.title}
				message={confirmOptions.message}
				confirmText={confirmOptions.confirmText}
				cancelText={confirmOptions.cancelText}
				variant={confirmOptions.variant}
				onConfirm={handleConfirm}
				onCancel={handleConfirmCancel}
			/>
		</div>
	);
};
